import { useQuery } from '@tanstack/react-query';
import type { UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { api } from '@/lib/api';
import {
  COURSE_FORM_TABS,
  COURSE_STATUS_OPTIONS,
  COURSE_VISIBILITY_OPTIONS,
  slugPlaceholder,
  type CourseFormTabId,
} from './course-form-constants';

export interface CourseFormValues {
  name: string;
  code: string;
  slug: string;
  status: string;
  shortDescription: string;
  fullDescription: string;
  categoryId: string;
  visibility: string;
  primaryInstructorId: string;
  additionalInstructors: string[];
  supervisorUserIds: string[];
  instructorRoleIds: string[];
  isFree: boolean;
  price: string;
  requiresEnrollment: boolean;
  allowComments: boolean;
  showProgress: boolean;
  minCompliancePercent: string;
  academicHoursTime: string;
  startDate: string;
  endDate: string;
  maxEnrollment: string;
  metaTitle: string;
  metaDescription: string;
  contractTemplateId: string;
  requiresPrerequisiteCourse: boolean;
  prerequisiteCourseId: string;
  prerequisiteAssessmentId: string;
}

interface CategoryOption {
  value: string;
  label: string;
}

interface InstructorOption {
  id: string;
  fullName: string;
}

interface RoleOption {
  id: string;
  label: string;
  userCount: number;
}

interface ContractTemplateOption {
  id: string;
  title: string;
}

interface CourseFormModernProps {
  register: UseFormRegister<CourseFormValues>;
  watch: UseFormWatch<CourseFormValues>;
  setValue: UseFormSetValue<CourseFormValues>;
  activeTab: CourseFormTabId;
  setActiveTab: (tab: CourseFormTabId) => void;
  categoryOptions: CategoryOption[];
  instructors: InstructorOption[];
  supervisors?: InstructorOption[];
  roles?: RoleOption[];
  contractTemplates?: ContractTemplateOption[];
  coverLabel: string;
  bannerLabel: string;
  courseId?: string;
  saveError?: string;
  onCoverChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBannerChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  readOnly?: boolean;
}

export function CourseFormModern({
  register,
  watch,
  setValue,
  activeTab,
  setActiveTab,
  categoryOptions,
  instructors,
  supervisors = [],
  roles = [],
  contractTemplates = [],
  coverLabel,
  bannerLabel,
  courseId,
  saveError,
  onCoverChange,
  onBannerChange,
  readOnly = false,
}: CourseFormModernProps) {
  const name = watch('name');
  const code = watch('code');
  const isFree = watch('isFree');
  const requiresPrerequisiteCourse = watch('requiresPrerequisiteCourse');
  const prerequisiteCourseId = watch('prerequisiteCourseId');
  const additionalInstructors = watch('additionalInstructors') ?? [];
  const supervisorUserIds = watch('supervisorUserIds') ?? [];
  const instructorRoleIds = watch('instructorRoleIds') ?? [];

  function toggleAdditionalInstructor(instructorId: string, checked: boolean) {
    const current = new Set(additionalInstructors);
    if (checked) current.add(instructorId);
    else current.delete(instructorId);
    setValue('additionalInstructors', Array.from(current), { shouldDirty: true });
  }

  function toggleSupervisor(userId: string, checked: boolean) {
    const current = new Set(supervisorUserIds);
    if (checked) current.add(userId);
    else current.delete(userId);
    setValue('supervisorUserIds', Array.from(current), { shouldDirty: true });
  }

  const { data: courseOptions } = useQuery({
    queryKey: ['courses', 'prerequisite-options'],
    queryFn: () => api<{ data: { id: string; name: string; code?: string }[] }>('/courses?perPage=100'),
    enabled: requiresPrerequisiteCourse,
  });
  const { data: assessmentOptions } = useQuery({
    queryKey: ['courses', prerequisiteCourseId, 'prerequisite-assessments'],
    queryFn: () =>
      api<{ data: { id: string; title: string; status?: string }[] }>(
        `/courses/${prerequisiteCourseId}/prerequisite-assessments`,
      ),
    enabled: requiresPrerequisiteCourse && Boolean(prerequisiteCourseId),
  });
  const otherCourses = (courseOptions?.data ?? []).filter((course) => course.id !== courseId);

  function toggleInstructorRole(roleId: string, checked: boolean) {
    const current = new Set(instructorRoleIds);
    if (checked) current.add(roleId);
    else current.delete(roleId);
    setValue('instructorRoleIds', Array.from(current), { shouldDirty: true });
  }

  return (
    <div className="lms-course-form__grid">
      <aside className="lms-course-form__aside">
        <nav className="lms-course-form__nav" aria-label="Secciones del formulario">
          {COURSE_FORM_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`lms-course-form__tab ${activeTab === tab.id ? 'lms-course-form__tab--active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                {tab.icon}
              </span>
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </aside>

      <fieldset disabled={readOnly} className="lms-course-form__body m-0 min-w-0 border-0 p-0">
        {activeTab === 'general' && (
          <div className="lms-course-form__panels">
            <section className="lms-course-form__card">
              <h2 className="lms-course-form__card-title">Información General</h2>
              <div className="lms-course-form__fields">
                <div className="lms-course-form__field lms-course-form__field--full">
                  <label className="lms-course-form__label" htmlFor="course-name">
                    Nombre <span className="text-[#ba1a1a]">*</span>
                  </label>
                  <input
                    id="course-name"
                    type="text"
                    required
                    placeholder="Ej. Arquitectura de Sistemas Distribuidos"
                    className="lms-course-form__input"
                    {...register('name', { required: true })}
                  />
                </div>
                <div className="lms-course-form__field">
                  <label className="lms-course-form__label" htmlFor="course-code">
                    Código <span className="text-[#ba1a1a]">*</span>
                  </label>
                  <input
                    id="course-code"
                    type="text"
                    required
                    placeholder="CS-202"
                    className="lms-course-form__input"
                    {...register('code', { required: true })}
                  />
                </div>
                <div className="lms-course-form__field">
                  <label className="lms-course-form__label" htmlFor="course-status">
                    Estado <span className="text-[#ba1a1a]">*</span>
                  </label>
                  <select
                    id="course-status"
                    required
                    className="lms-course-form__input lms-course-form__select"
                    {...register('status', { required: true })}
                  >
                    {COURSE_STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="lms-course-form__field lms-course-form__field--full">
                  <label className="lms-course-form__label" htmlFor="course-slug">
                    Slug
                  </label>
                  <input
                    id="course-slug"
                    type="text"
                    placeholder={slugPlaceholder(name, code)}
                    className="lms-course-form__input"
                    {...register('slug')}
                  />
                  <p className="lms-course-form__hint">Se genera como nombre-código si se deja vacío</p>
                </div>
                <div className="lms-course-form__field lms-course-form__field--full">
                  <label className="lms-course-form__label" htmlFor="course-short-description">
                    Descripción corta
                  </label>
                  <textarea
                    id="course-short-description"
                    rows={3}
                    placeholder="Resumen ejecutivo del curso..."
                    className="lms-course-form__input lms-course-form__textarea"
                    {...register('shortDescription')}
                  />
                </div>
              </div>
            </section>

            <div className="lms-course-form__bento">
              <section className="lms-course-form__card lms-course-form__card--wide">
                <h2 className="lms-course-form__card-title">Descripción completa</h2>
                <div className="lms-course-form__editor">
                  <div className="lms-course-form__editor-toolbar" aria-hidden="true">
                    <span className="material-symbols-outlined text-sm">format_bold</span>
                    <span className="material-symbols-outlined text-sm">format_italic</span>
                    <span className="material-symbols-outlined text-sm">format_list_bulleted</span>
                    <span className="lms-course-form__editor-divider" />
                    <span className="material-symbols-outlined text-sm">link</span>
                    <span className="material-symbols-outlined text-sm">image</span>
                  </div>
                  <textarea
                    id="course-full-description"
                    rows={12}
                    placeholder="Describa los objetivos pedagógicos, temario detallado y metodología..."
                    className="lms-course-form__editor-area"
                    {...register('fullDescription')}
                  />
                </div>
              </section>

              <div className="lms-course-form__media-stack">
                <section className="lms-course-form__card">
                  <label className="lms-course-form__label mb-4 block">Imagen de portada</label>
                  <label className="lms-course-form__upload lms-course-form__upload--tall" htmlFor="course-cover">
                    <span className="lms-course-form__upload-icon">
                      <span className="material-symbols-outlined text-[#001c39]" aria-hidden="true">
                        cloud_upload
                      </span>
                    </span>
                    <p className="text-sm font-medium text-[#1a1c1e]">{coverLabel}</p>
                    <p className="mt-1 text-[10px] text-[#454651]">PNG, JPG hasta 5MB</p>
                    <input
                      id="course-cover"
                      type="file"
                      accept="image/*"
                      className="sr-only"
                      onChange={onCoverChange}
                    />
                  </label>
                </section>

                <section className="lms-course-form__card">
                  <label className="lms-course-form__label mb-4 block">Banner opcional</label>
                  <label className="lms-course-form__upload lms-course-form__upload--banner" htmlFor="course-banner">
                    <span className="material-symbols-outlined mb-2 text-[#454651]" aria-hidden="true">
                      add_photo_alternate
                    </span>
                    <p className="text-xs font-semibold text-[#454651]">{bannerLabel}</p>
                    <input
                      id="course-banner"
                      type="file"
                      accept="image/*"
                      className="sr-only"
                      onChange={onBannerChange}
                    />
                  </label>
                </section>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'organization' && (
          <section className="lms-course-form__card">
            <h2 className="lms-course-form__card-title">Organización</h2>
            <div className="lms-course-form__fields">
              <div className="lms-course-form__field">
                <label className="lms-course-form__label" htmlFor="course-category">
                  Categoría <span className="text-[#ba1a1a]">*</span>
                </label>
                <select
                  id="course-category"
                  required
                  className="lms-course-form__input lms-course-form__select"
                  {...register('categoryId', { required: true })}
                >
                  <option value="">Seleccionar categoría</option>
                  {categoryOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="lms-course-form__field">
                <label className="lms-course-form__label" htmlFor="course-visibility">
                  Visibilidad <span className="text-[#ba1a1a]">*</span>
                </label>
                <select
                  id="course-visibility"
                  required
                  className="lms-course-form__input lms-course-form__select"
                  {...register('visibility', { required: true })}
                >
                  {COURSE_VISIBILITY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="lms-course-form__field lms-course-form__field--full">
                <label className="lms-course-form__label" htmlFor="course-instructor">
                  Instructor principal <span className="text-[#ba1a1a]">*</span>
                </label>
                <select
                  id="course-instructor"
                  required
                  className="lms-course-form__input lms-course-form__select"
                  {...register('primaryInstructorId', { required: true })}
                >
                  <option value="">Seleccionar instructor</option>
                  {instructors.map((instructor) => (
                    <option key={instructor.id} value={instructor.id}>
                      {instructor.fullName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="lms-course-form__field lms-course-form__field--full">
                <span className="lms-course-form__label">
                  Instructores adicionales <span className="lms-course-form__label-muted">(Opcional)</span>
                </span>
                <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {instructors.map((instructor) => (
                    <label key={instructor.id} className="flex items-center gap-2 text-sm text-[#454651]">
                      <input
                        type="checkbox"
                        className="rounded border-[#c6c5d3]"
                        checked={additionalInstructors.includes(instructor.id)}
                        onChange={(event) => toggleAdditionalInstructor(instructor.id, event.target.checked)}
                      />
                      {instructor.fullName}
                    </label>
                  ))}
                </div>
              </div>
              <div className="lms-course-form__field lms-course-form__field--full">
                <span className="lms-course-form__label">
                  Supervisores (solo lectura) <span className="lms-course-form__label-muted">(Opcional)</span>
                </span>
                {supervisors.length === 0 ? (
                  <p className="mt-2 text-sm text-[#454651]">No hay supervisores disponibles.</p>
                ) : (
                  <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {supervisors.map((supervisor) => (
                      <label key={supervisor.id} className="flex items-center gap-2 text-sm text-[#454651]">
                        <input
                          type="checkbox"
                          className="rounded border-[#c6c5d3]"
                          checked={supervisorUserIds.includes(supervisor.id)}
                          onChange={(event) => toggleSupervisor(supervisor.id, event.target.checked)}
                        />
                        {supervisor.fullName}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div className="lms-course-form__field lms-course-form__field--full">
                <span className="lms-course-form__label">
                  Agregar por rol <span className="lms-course-form__label-muted">(Opcional)</span>
                </span>
                {roles.length === 0 ? (
                  <p className="mt-2 text-sm text-[#454651]">No hay roles disponibles.</p>
                ) : (
                  <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {roles.map((role) => (
                      <label key={role.id} className="flex items-center gap-2 text-sm text-[#454651]">
                        <input
                          type="checkbox"
                          className="rounded border-[#c6c5d3]"
                          checked={instructorRoleIds.includes(role.id)}
                          onChange={(event) => toggleInstructorRole(role.id, event.target.checked)}
                        />
                        <span>
                          {role.label}
                          <span className="text-[#767682]">
                            {' '}
                            · {role.userCount} {role.userCount === 1 ? 'usuario' : 'usuarios'}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {activeTab === 'configuration' && (
          <section className="lms-course-form__card">
            <h2 className="lms-course-form__card-title lms-course-form__card-title--spaced">Configuración</h2>
            <div className="lms-course-form__config">
              <section className="lms-course-form__section">
                <h3 className="lms-course-form__section-title">Ajustes Generales</h3>
                <div className="lms-course-form__section-body">
                  <div className="lms-course-form__toggle-row">
                    <p className="lms-course-form__toggle-label">Curso gratuito</p>
                    <label className="lms-course-form__switch">
                      <input type="checkbox" {...register('isFree')} />
                      <span />
                    </label>
                  </div>
                  <div className="lms-course-form__field">
                    <label className="lms-course-form__label" htmlFor="course-price">
                      Precio (si es de pago)
                    </label>
                    <div className="lms-course-form__price-wrap">
                      <span className="lms-course-form__price-prefix" aria-hidden="true">$</span>
                      <input
                        id="course-price"
                        type="number"
                        step="0.01"
                        min="0"
                        disabled={isFree}
                        className="lms-course-form__input lms-course-form__input--price"
                        {...register('price')}
                      />
                    </div>
                  </div>
                </div>
              </section>

              <hr className="lms-course-form__divider" />

              <section className="lms-course-form__section">
                <h3 className="lms-course-form__section-title">Plantilla de contrato</h3>
                <div className="lms-course-form__section-body">
                  <div className="lms-course-form__field">
                    <select
                      id="course-contract-template"
                      className="lms-course-form__input lms-course-form__select"
                      {...register('contractTemplateId')}
                    >
                      <option value="">Sin contrato automático</option>
                      {contractTemplates.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.title}
                        </option>
                      ))}
                    </select>
                    <p className="lms-course-form__hint">
                      Si se selecciona, se generará un contrato al matricular alumnos en este curso.
                    </p>
                  </div>
                </div>
              </section>

              <hr className="lms-course-form__divider" />

              <section className="lms-course-form__section">
                <h3 className="lms-course-form__section-title">Opciones de configuración</h3>
                <div className="lms-course-form__section-body lms-course-form__section-body--toggles">
                  <div className="lms-course-form__toggle-row">
                    <span className="lms-course-form__toggle-text">Requiere inscripción</span>
                    <label className="lms-course-form__switch">
                      <input type="checkbox" {...register('requiresEnrollment')} />
                      <span />
                    </label>
                  </div>
                  <div className="lms-course-form__toggle-row">
                    <span className="lms-course-form__toggle-text">Permitir comentarios</span>
                    <label className="lms-course-form__switch">
                      <input type="checkbox" {...register('allowComments')} />
                      <span />
                    </label>
                  </div>
                  <div className="lms-course-form__toggle-row">
                    <span className="lms-course-form__toggle-text">Mostrar progreso</span>
                    <label className="lms-course-form__switch">
                      <input type="checkbox" {...register('showProgress')} />
                      <span />
                    </label>
                  </div>
                </div>
              </section>
            </div>
          </section>
        )}

        {activeTab === 'requirements' && (
          <section className="lms-course-form__card">
            <h2 className="lms-course-form__card-title">Requisitos</h2>
            <div className="lms-course-form__fields">
              {saveError ? (
                <p className="lms-course-form__field--full text-sm text-red-700" role="alert">
                  {saveError}
                </p>
              ) : null}
              <div className="lms-course-form__toggle-row lms-course-form__field--full">
                <span className="lms-course-form__toggle-text">Requiere curso previo</span>
                <label className="lms-course-form__switch">
                  <input
                    id="course-requires-prerequisite"
                    type="checkbox"
                    {...register('requiresPrerequisiteCourse')}
                  />
                  <span />
                </label>
              </div>
              {requiresPrerequisiteCourse ? (
                <>
                  <div className="lms-course-form__field lms-course-form__field--full">
                    <label className="lms-course-form__label" htmlFor="course-prerequisite">
                      Curso previo
                    </label>
                    <select
                      id="course-prerequisite"
                      className="lms-course-form__input lms-course-form__select"
                      value={prerequisiteCourseId}
                      onChange={(event) => {
                        setValue('prerequisiteCourseId', event.target.value, { shouldDirty: true });
                        setValue('prerequisiteAssessmentId', '', { shouldDirty: true });
                      }}
                    >
                      <option value="">Selecciona un curso</option>
                      {otherCourses.map((course) => (
                        <option key={course.id} value={course.id}>
                          {course.code ? `${course.code} — ${course.name}` : course.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="lms-course-form__field lms-course-form__field--full">
                    <label className="lms-course-form__label" htmlFor="course-prerequisite-assessment">
                      Evaluación
                    </label>
                    <select
                      id="course-prerequisite-assessment"
                      className="lms-course-form__input lms-course-form__select"
                      disabled={!prerequisiteCourseId}
                      {...register('prerequisiteAssessmentId')}
                    >
                      <option value="">Selecciona una evaluación</option>
                      {(assessmentOptions?.data ?? []).map((assessment) => (
                        <option key={assessment.id} value={assessment.id}>
                          {assessment.status && assessment.status !== 'published'
                            ? `${assessment.title} (borrador)`
                            : assessment.title}
                        </option>
                      ))}
                    </select>
                    <p className="lms-course-form__hint">
                      El alumno debe tener aprobada esta evaluación (revisión manual) para acceder al contenido.
                    </p>
                  </div>
                </>
              ) : null}
            </div>
          </section>
        )}

        {activeTab === 'academic' && (
          <section className="lms-course-form__card">
            <h2 className="lms-course-form__card-title">Datos académicos</h2>
            <div className="lms-course-form__fields">
              <div className="lms-course-form__field">
                <label className="lms-course-form__label" htmlFor="course-min-compliance-percent">
                  Porcentaje mínimo
                </label>
                <input
                  id="course-min-compliance-percent"
                  type="number"
                  min={0}
                  max={100}
                  step={0.01}
                  placeholder="40"
                  className="lms-course-form__input"
                  {...register('minCompliancePercent')}
                />
              </div>
              <div className="lms-course-form__field">
                <label className="lms-course-form__label" htmlFor="course-academic-hours">
                  Duración del curso
                </label>
                <input
                  id="course-academic-hours"
                  type="number"
                  min={0}
                  step={0.01}
                  placeholder="150"
                  className="lms-course-form__input"
                  {...register('academicHoursTime')}
                />
              </div>
              <div className="lms-course-form__field">
                <label className="lms-course-form__label" htmlFor="course-start">
                  Fecha de inicio
                </label>
                <input id="course-start" type="date" className="lms-course-form__input" {...register('startDate')} />
              </div>
              <div className="lms-course-form__field">
                <label className="lms-course-form__label" htmlFor="course-end">
                  Fecha de finalización
                </label>
                <input id="course-end" type="date" className="lms-course-form__input" {...register('endDate')} />
              </div>
              <div className="lms-course-form__field">
                <label className="lms-course-form__label" htmlFor="course-max">
                  Cupo máximo <span className="lms-course-form__label-muted">(Opcional)</span>
                </label>
                <input
                  id="course-max"
                  type="number"
                  min={1}
                  className="lms-course-form__input"
                  {...register('maxEnrollment')}
                />
              </div>
            </div>
          </section>
        )}

        {activeTab === 'seo' && (
          <section className="lms-course-form__card">
            <h2 className="lms-course-form__card-title">SEO</h2>
            <div className="lms-course-form__fields">
              <div className="lms-course-form__field lms-course-form__field--full">
                <label className="lms-course-form__label" htmlFor="course-meta-title">
                  Meta título
                </label>
                <input id="course-meta-title" type="text" className="lms-course-form__input" {...register('metaTitle')} />
              </div>
              <div className="lms-course-form__field lms-course-form__field--full">
                <label className="lms-course-form__label" htmlFor="course-meta-description">
                  Meta descripción
                </label>
                <textarea
                  id="course-meta-description"
                  rows={4}
                  className="lms-course-form__input lms-course-form__textarea"
                  {...register('metaDescription')}
                />
              </div>
            </div>
          </section>
        )}
      </fieldset>
    </div>
  );
}
